// Shared money and label formatting.
//
// Amounts were previously rendered with bare toFixed(2), so a lending app showed
// "$25000.00". One formatter, used everywhere, so figures group correctly and the
// currency is stated once.

const CURRENCY = "CAD";
const LOCALE = "en-CA";

const fmt = new Intl.NumberFormat(LOCALE, {
  style: "currency",
  currency: CURRENCY,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export function money(v) {
  const n = Number(v);
  if (v === null || v === undefined || Number.isNaN(n)) return "—";
  return fmt.format(n);
}

/** Compact form for dashboard tiles: $1.2M, $45.3k. Falls back to money(). */
export function moneyShort(v) {
  const n = Number(v);
  if (v === null || v === undefined || Number.isNaN(n)) return "—";
  const a = Math.abs(n);
  if (a >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (a >= 10_000) return `$${(n / 1000).toFixed(1)}k`;
  return money(n);
}

export function pct(v, digits = 1) {
  const n = Number(v);
  if (Number.isNaN(n)) return "—";
  return `${n.toFixed(digits)}%`;
}

export function shortDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString(LOCALE, { year: "numeric", month: "short", day: "numeric" });
}

// Statuses were title-cased from snake_case, which rendered "Awaiting Ekyc".
// Anything not named here falls back to the old behaviour.
const STATUS_LABELS = {
  draft: "Draft",
  awaiting_ekyc: "Awaiting eKYC",
  awaiting_documents: "Awaiting documents",
  submitted: "Submitted",
  under_review: "Under review",
  more_info_requested: "More info requested",
  recommended_for_approval: "Recommended",
  approved: "Approved",
  rejected: "Rejected",
  cancelled: "Cancelled",
  disbursement_pending: "Pending disbursement",
  active: "Active",
  completed: "Completed",
  closed: "Closed",
  written_off: "Written off",
  // servicing
  due: "Due",
  partial: "Partially paid",
  overdue: "Overdue",
  paid: "Paid",
  posted: "Posted",
  voided: "Voided",
  open: "Open",
};

export function statusLabel(s) {
  if (!s) return "—";
  if (STATUS_LABELS[s]) return STATUS_LABELS[s];
  return String(s).replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

const BUCKET_STYLES = {
  current: "bg-emerald-50 text-emerald-700 border-emerald-200",
  "1-30": "bg-amber-50 text-amber-700 border-amber-200",
  "31-60": "bg-orange-50 text-orange-700 border-orange-200",
  "61-90": "bg-red-50 text-red-700 border-red-200",
  "90+": "bg-red-100 text-red-800 border-red-300",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
  written_off: "bg-slate-200 text-slate-700 border-slate-300",
};

export function bucketClass(b) {
  return BUCKET_STYLES[b] || "bg-slate-100 text-slate-600 border-slate-200";
}

const STATUS_STYLES = {
  paid: "bg-emerald-50 text-emerald-700 border-emerald-200",
  due: "bg-slate-50 text-slate-600 border-slate-200",
  partial: "bg-amber-50 text-amber-700 border-amber-200",
  overdue: "bg-red-50 text-red-700 border-red-200",
  posted: "bg-emerald-50 text-emerald-700 border-emerald-200",
  voided: "bg-slate-100 text-slate-500 border-slate-200 line-through",
  active: "bg-blue-50 text-blue-700 border-blue-200",
  closed: "bg-slate-100 text-slate-600 border-slate-200",
  written_off: "bg-slate-200 text-slate-700 border-slate-300",
};

export function statusClass(s) {
  return STATUS_STYLES[s] || "bg-slate-100 text-slate-600 border-slate-200";
}

export function Pill({ children, className = "" }) {
  return (
    <span className={"inline-block text-xs font-semibold px-2 py-0.5 rounded-full border " + className}>
      {children}
    </span>
  );
}
